import React, { useState, useRef, useEffect } from 'react'
import { api, getToken } from './api.js'
import AppIcon from './components/AppIcon.jsx'

const STATUS_PL = { queued: 'w kolejce', running: 'przetwarzanie', done: 'gotowe', error: 'błąd', canceled: 'anulowane' }
export const StatusPill = ({ status }) => <span className={`pill pill-${status}`}>{STATUS_PL[status] || status}</span>
export const Progress = ({ value }) => <div className="progress"><div className="progress-fill" style={{ width: `${value || 0}%` }} /></div>

// Backend (Pydantic) serializuje naiwne UTC daty BEZ sufiksu strefy (brak "Z"/"+00:00") — bez tego
// przeglądarka parsuje taki string jako czas LOKALNY, nie UTC, co przy Polsce (UTC+2 latem) dawało
// zawsze ~120 minut "upływu" na starcie zadania (błąd zgłoszony 2026-08-18). Doklejamy "Z", gdy
// string nie ma już żadnego znacznika strefy.
function _parseServerUtc(s) {
  if (!s) return NaN
  const iso = /[Zz]|[+-]\d\d:\d\d$/.test(s) ? s : s + 'Z'
  return new Date(iso).getTime()
}

// Upływający czas (mm:ss) liczony od `started_at`/`created_at` joba — współdzielone przez
// `JobProgress` i przez alerty "w toku", które chcą pokazać czas wyrównany do prawej we WŁASNYM
// wierszu (patrz `ProjectWorkspace.jsx`'s `ContentTab`), zamiast w osobnej linii pod paskiem.
export function useJobElapsed(job) {
  const [elapsed, setElapsed] = useState(0)
  const terminal = job && (job.status === 'done' || job.status === 'error' || job.status === 'canceled')

  useEffect(() => {
    if (!job || terminal) return undefined
    const startedAt = _parseServerUtc(job.started_at || job.created_at)
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)))
    tick()
    const t = setInterval(tick, 1000)
    return () => clearInterval(t)
  }, [job?.id, job?.status, job?.started_at, job?.created_at, terminal])

  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0')
  const ss = String(elapsed % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

// Pasek postępu joba (import/kompozycja/zadania AI szablonu) z etapem i upływającym czasem —
// trwały odpowiednik samego spinnera, żeby po odświeżeniu strony (Faza 10, wznowienie polowania
// z `active-job`) użytkownik widział to samo, co przed odświeżeniem. `null`/brak joba renderuje
// nic (uproszcza użycie w JSX). `showTime=false`, gdy czas jest już pokazany osobno (np. wyrównany
// do prawej w otaczającym `Alert kind="info"` — patrz `useJobElapsed` powyżej), żeby się nie dublował.
export function JobProgress({ job, showTime = true }) {
  const time = useJobElapsed(job)
  if (!job) return null
  if (job.status === 'error') return <Alert kind="err">{job.error_msg}</Alert>

  return (
    <div className="job-progress">
      <Progress value={job.progress} />
      {job.stage && <div className="muted small" style={{ marginTop: 6 }}>{job.stage}</div>}
      {showTime && <div className="muted small mono" style={{ marginTop: 2 }}>{time}</div>}
    </div>
  )
}

export function VerdictBadge({ verdict }) {
  if (!verdict) return null
  const cls = verdict === 'PASS' ? 'verdict-pass' : verdict === 'FAIL' ? 'verdict-fail' : 'verdict-warn'
  return <span className={`verdict ${cls}`}>{verdict.replace(/_/g, ' ')}</span>
}

export function Card({ title, desc, right, children }) {
  return (
    <div className="card">
      {(title || right) && (
        <div className="card-head">
          <div>{title && <h2>{title}</h2>}{desc && <div className="desc">{desc}</div>}</div>
          {right}
        </div>
      )}
      {children}
    </div>
  )
}

export function Field({ label, hint, children }) {
  return <div className="field"><label>{label}</label>{children}{hint && <div className="hint">{hint}</div>}</div>
}

export function Advanced({ children, label = 'Opcje zaawansowane' }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="adv">
      <span className="adv-toggle" onClick={() => setOpen(o => !o)}>{open ? '▾' : '▸'} {label}</span>
      {open && <div className="adv-body">{children}</div>}
    </div>
  )
}

export function FileField({ label, accept, file, onPick, hint }) {
  const ref = useRef()
  return (
    <Field label={label} hint={hint}>
      <div className="dropzone" onClick={() => ref.current?.click()} style={{ cursor: 'pointer' }}>
        <div className="muted small">{file ? 'Kliknij, aby zmienić plik' : `Kliknij i wybierz plik (${accept})`}</div>
        {file && <div className="fn">{file.name} · {(file.size / 1048576).toFixed(1)} MB</div>}
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => onPick(e.target.files?.[0] || null)} />
      </div>
    </Field>
  )
}

export const Alert = ({ kind = 'info', children }) => children ? <div className={`alert alert-${kind}`}>{children}</div> : null

// Wyszukiwarka użytkowników systemu po username/email (nie ma osobnych pól imię/nazwisko) - do
// wyboru komu udostępnić projekt / dla kogo admin tworzy projekt. Debounce 300ms, min. 2 znaki.
export function UserSearch({ onPick, placeholder = 'Szukaj po nazwie użytkownika lub e-mailu…', exclude = [] }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const timer = useRef()

  useEffect(() => {
    clearTimeout(timer.current)
    if (q.trim().length < 2) { setResults([]); return undefined }
    timer.current = setTimeout(async () => {
      setBusy(true)
      try { setResults(await api.searchUsers(q.trim())) } catch { setResults([]) } finally { setBusy(false) }
    }, 300)
    return () => clearTimeout(timer.current)
  }, [q])

  const filtered = results.filter(u => !exclude.includes(u.id))

  return (
    <div style={{ position: 'relative' }}>
      <input value={q} onChange={e => { setQ(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder={placeholder} autoComplete="off" />
      {open && q.trim().length >= 2 && (
        <div className="card" style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0,
          marginTop: 4, maxHeight: 240, overflowY: 'auto', padding: 6 }}>
          {busy && <div className="muted small" style={{ padding: 8 }}>Szukanie…</div>}
          {!busy && filtered.length === 0 && <div className="muted small" style={{ padding: 8 }}>Brak wyników</div>}
          {filtered.map(u => (
            <div key={u.id} className="unit-row" style={{ cursor: 'pointer', marginBottom: 4, padding: '8px 10px' }}
              onMouseDown={e => e.preventDefault()} onClick={() => { onPick(u); setQ(''); setResults([]); setOpen(false) }}>
              <b>{u.username}</b> <span className="muted small">{u.email}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="card-head">
          <h3>{title}</h3>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Zamknij ✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

export function usePager(items, perPage = 10) {
  const [page, setPage] = useState(1)
  const list = Array.isArray(items) ? items : []
  const pages = Math.max(1, Math.ceil(list.length / perPage))
  const p = Math.min(page, pages)
  return { page: p, setPage, pages, total: list.length, slice: list.slice((p - 1) * perPage, p * perPage) }
}
export function Pager({ page, pages, setPage, total }) {
  if (pages <= 1) return null
  return (
    <div className="pager">
      <button type="button" className="btn btn-sm btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Poprzednia</button>
      <span className="muted small">Strona {page} z {pages}{total != null ? ` · ${total} poz.` : ''}</span>
      <button type="button" className="btn btn-sm btn-ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Następna ›</button>
    </div>
  )
}

// Dzwonek powiadomień — polling co 30s licznika nieprzeczytanych + rozwijana lista ostatnich.
// Musi cicho zniknąć (render null) przy dowolnym błędzie API, nigdy nie psuć layoutu Layout.jsx —
// powiadomienia to funkcja pomocnicza, nie coś co ma zablokować cały panel przy awarii backendu.
export function NotificationsBell() {
  const [count, setCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [broken, setBroken] = useState(false)

  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      if (!getToken()) return
      try { const r = await api.unreadCount(); if (!cancelled) setCount(r.count) }
      catch { if (!cancelled) setBroken(true) }
    }
    tick()
    const t = setInterval(tick, 30000)
    return () => { cancelled = true; clearInterval(t) }
  }, [])

  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (next) {
      try { setItems(await api.notifications({ limit: 10 })) } catch { setBroken(true) }
    }
  }

  const markRead = async (id) => {
    try { await api.markNotificationRead(id); setItems(list => list.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n)); setCount(c => Math.max(0, c - 1)) }
    catch { /* ignoruj — dzwonek nie ma psuć layoutu */ }
  }

  const markAll = async () => {
    try { await api.markAllNotificationsRead(); setItems(list => list.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))); setCount(0) }
    catch { /* ignoruj */ }
  }

  if (broken) return null

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="btn btn-ghost btn-sm notifications-toggle" title="Powiadomienia" aria-label="Powiadomienia" onClick={toggle}>
        <AppIcon name="bell"/><span className="notification-label">Powiadomienia</span>
        {count > 0 && <span className="pill" style={{ color: 'var(--err)', borderColor: '#e5c3c3' }}>{count}</span>}
      </button>
      {open && (
        <div className="card" style={{ position: 'absolute', zIndex: 40, bottom: '100%', left: 0, right: 0,
          marginBottom: 6, maxHeight: 320, overflowY: 'auto', padding: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <b className="small">Ostatnie powiadomienia</b>
            <button type="button" className="btn btn-sm btn-ghost" onClick={markAll}>Oznacz wszystkie</button>
          </div>
          {items.length === 0 ? <div className="muted small">Brak powiadomień.</div> : items.map(n => (
            <div key={n.id} className="unit-row" style={{ opacity: n.read_at ? 0.6 : 1 }}>
              <div className="small" style={{ fontWeight: 700 }}>{n.title_key}</div>
              <div className="muted small">{n.body_key}</div>
              {!n.read_at && <button type="button" className="btn btn-sm btn-ghost" style={{ marginTop: 6 }} onClick={() => markRead(n.id)}>Oznacz jako przeczytane</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Prosty poller joba w tle (setTimeout, jak Chain.jsx w platform-web) — wywołuje fetchJob(id) co
// `interval` ms dopóki status nie osiągnie done/error, wtedy woła onDone(job)/onError(job).
//
// `timeoutMs` (domyślnie 5 min) to backstop KLIENTA, niezależny od backendu: backend ma własny
// "leniwy watchdog" (patrz `app/jobs.py::_check_stuck`) który zamyka zawieszone zadania statusem
// "error" przy samym odczycie — to załatwia normalny przypadek (worker padł) i przetrwa nawet
// odświeżenie strony. Ten timeout tu jest na wypadek gdyby TA konkretna pętla pollingu nigdy nie
// dostała odpowiedzi z powrotem (np. problem sieciowy między przeglądarką a serwerem), albo gdyby
// hook był kiedyś użyty z innym backendem bez takiego watchdogu — obie warstwy są potrzebne,
// żadna sama w sobie nie wystarcza. Liczone od PIERWSZEGO ticka (Date.now() raz, nie per-interval)
// — to prawdziwy zegar ścienny, nie liczba prób (ważne przy chwilowych opóźnieniach interwału).
export function usePolling(fetchJob, id, { interval = 2000, onDone, onError, timeoutMs = 300000 } = {}) {
  const [job, setJob] = useState(null)
  const timer = useRef()
  React.useEffect(() => {
    if (!id) return undefined
    let cancelled = false
    const startedAt = Date.now()
    const tick = async () => {
      try {
        const j = await fetchJob(id)
        if (cancelled) return
        setJob(j)
        if (j.status === 'done') { onDone && onDone(j); return }
        if (j.status === 'error') { onError && onError(j); return }
      } catch { /* retry na kolejnym tick */ }
      if (cancelled) return
      if (Date.now() - startedAt > timeoutMs) {
        onError && onError({
          status: 'error',
          error_msg: 'Sprawdzanie statusu trwa zbyt długo — sprawdź później lub spróbuj ponownie.',
          timedOut: true,
        })
        return
      }
      timer.current = setTimeout(tick, interval)
    }
    tick()
    return () => { cancelled = true; clearTimeout(timer.current) }
  }, [id])
  return job
}
